'use client'

import { camelCase, debounce, get } from 'lodash'
import { AlertCircleIcon, BadgeCheck, Loader2 } from 'lucide-react'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { Popover as ArcoPopover, Slider } from '@arco-design/web-react'

import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  TBundle,
  TPlanOption,
  TPlanOptionSpec,
  TProjectBundle,
} from '@/apis/typings'
import {
  PlanControllerCalculatePrice,
  PlanControllerGetPlanOptions,
} from '@/apis/v1/plans'
import {
  ProjectControllerCreate,
  ProjectControllerFindAppIdExists,
} from '@/apis/v1/project'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BundleType, BundleTypeText } from '@/constants'
import ChargeButton from '@/pages/app/billings/recharge/charge-button'
import { queryKeys, useAccountQuery } from '@/pages/app/service'
import { useModel } from '@/store'
import { cn } from '@/utils'
import { formatOriginalPrice, formatPrice, formatSize } from '@/utils/format'
import {
  CaretSortIcon,
  CheckIcon,
  PlusCircledIcon,
} from '@radix-ui/react-icons'
import slugify from '@sindresorhus/slugify'

type PopoverTriggerProps = React.ComponentPropsWithoutRef<typeof PopoverTrigger>

interface TeamSwitcherProps {
  type?: 'create' | 'edit'
  className?: string
}

export interface TypeBundle {
  store: number
  cloud: number
  miniPrograms: number
  storage: number
  database: number
  bundleId?: string
}

export default function TeamSwitcher({
  className,
  type = 'create',
}: TeamSwitcherProps) {
  const [open, setOpen] = React.useState(false)
  const {
    projects,
    currentProject,
    showError,
    addProjectModalVisible,
    changeAddProjectModal,
    setCurrentProject,
    showSuccess,
    updateProjects,
    scenes,
    // updateProjectId,
  } = useModel((state) => state)
  const { data: accountRes } = useAccountQuery()

  const createProjectMutation = useMutation({
    mutationFn: (params: any) => ProjectControllerCreate(params),
  })
  const [slugError, setSlugError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)
  const [calculating, setCalculating] = useState(false)

  const [data, setData] = useState<{
    name: string
    appid: string
    scene: string
  }>({
    name: '',
    appid: '',
    scene: scenes?.[0]._id || '',
  })
  const { name, appid, scene } = data

  const bundles = React.useMemo(() => {
    if (!scenes?.length || !scene) {
      return []
    }
    const _bundles = scenes?.find((item) => item._id === scene)?.bundles ?? []
    return [
      ..._bundles,
      {
        _id: 'custom',
        sceneId: scene,
        name: 'custom',
        displayName: '自定义版',
        spec: {
          store: {
            value: -1,
          },
          'mini-programs': {
            value: -1,
          },
          storage: {
            value: -1,
          },
          cloud: {
            value: -1,
          },
        },
      } as TBundle,
    ]
  }, [scene, scenes])

  const defaultBundle: TypeBundle = {
    cloud:
      currentProject?.bundle?.resource?.cloud ||
      bundles?.[0]?.spec?.cloud.value,
    store:
      currentProject?.bundle?.resource?.limitStore ||
      bundles?.[0]?.spec?.store.value,
    storage:
      currentProject?.bundle?.resource?.storageCapacity ||
      bundles?.[0]?.spec?.storage.value,
    miniPrograms:
      currentProject?.bundle?.resource?.limitMiniPrograms ||
      bundles?.[0]?.spec?.['mini-programs'].value,
    database:
      currentProject?.bundle?.resource?.databaseCapacity ||
      bundles?.[0]?.spec?.database.value,
    bundleId: bundles?.[0]?._id,
  }
  const [bundle, setBundle] = useState(defaultBundle)
  const findBundles = (projectBundle: TProjectBundle) => {
    const { resource } = projectBundle || {}
    const bundle = bundles.find((bundle) => {
      const {
        'mini-programs': miniPrograms,
        cloud,
        store,
        storage,
      } = bundle.spec
      return (
        cloud.value === resource?.cloud &&
        miniPrograms.value === resource?.limitMiniPrograms &&
        storage.value === resource?.storageCapacity &&
        store.value === resource?.limitStore
      )
    })
    if (!bundle) {
      return null
    }
    return bundle
  }
  useEffect(() => {
    if (type === 'create' || !currentProject || !bundles?.length) {
      return
    }
    const { bundle: projectBundle } = currentProject
    const bundle = findBundles(projectBundle as TProjectBundle)
    if (!bundle) {
      return
    }
    setBundle({
      cloud: bundle?.spec?.cloud.value,
      store: bundle?.spec?.store.value,
      storage: bundle?.spec?.storage.value,
      database: bundle?.spec?.database.value,
      miniPrograms: bundle?.spec?.['mini-programs'].value,
      bundleId: bundle?._id,
    })
  }, [type, currentProject, bundles])

  const billingQuery = useQuery({
    queryKey: [...queryKeys.useBillingPriceQuery, bundle, scene],
    queryFn: async () => {
      return PlanControllerCalculatePrice({
        sceneId: scene,
        ...bundle,
      })
    },
    enabled: false,
    staleTime: 1000,
  })

  useEffect(() => {
    if (billingQuery.data) {
      setTotalPrice(billingQuery.data?.data.total || 0)
      setCalculating(false)
    }
  }, [billingQuery])

  useEffect(() => {
    const debouncedBillingQuery = debounce(() => {
      billingQuery.refetch()
    }, 500)

    if (addProjectModalVisible) {
      debouncedBillingQuery()
    }

    return () => {
      debouncedBillingQuery.cancel()
    }
  }, [addProjectModalVisible, bundle])

  const [debouncedSlug] = useDebounce(appid, 500)

  useEffect(() => {
    if (debouncedSlug.length > 0 && !slugError) {
      ProjectControllerFindAppIdExists({ appid }).then((res) => {
        if (res.data) {
          setSlugError(res.data ? '项目AppId已经存在' : null)
        }
      })
    }
  }, [debouncedSlug, slugError])

  useEffect(() => {
    setSlugError(null)
    setData((prev) => ({
      ...prev,
      appid: slugify(name),
    }))
  }, [name])

  const { data: billingResourceOptionsRes, isLoading } = useQuery({
    queryKey: queryKeys.useBillingPlansOptionsQuery,
    queryFn: async () => {
      return PlanControllerGetPlanOptions({})
    },
    enabled: addProjectModalVisible,
  })

  const handleSubmit = async () => {
    if (!name || !appid) {
      showError('项目名称和appId不可缺少！')
      return
    }
    if (!scene) {
      showError('请选择项目类型，选择后不可更改')
      return
    }
    setSaving(true)
    try {
      await createProjectMutation.mutateAsync({
        name,
        appid,
        sceneId: scene,
        ...bundle,
      })
      showSuccess('项目创建成功！')
      updateProjects()
      // setData({ name: '', slug: '', type: ProjectType.Appointment })
      changeAddProjectModal(false)
    } catch (error) {
      showError('项目创建失败，请稍候重试')
    } finally {
      setSaving(false)
    }
  }

  const handleBundleSelect = (item: TBundle) => {
    if (item._id !== 'custom') {
      setCalculating(true)
      setBundle({
        cloud: item.spec.cloud.value,
        store: item.spec.store.value,
        storage: item.spec.storage.value,
        database: item.spec.database.value,
        miniPrograms: item.spec['mini-programs'].value,
        bundleId: item._id,
      })
    }
  }
  const handleCustomBundleSelect = (newBundle: TypeBundle) => {
    setCalculating(true)
    let bundle = bundles.find((bundle) => {
      const {
        'mini-programs': miniPrograms,
        cloud,
        store,
        storage,
        database,
      } = bundle.spec
      return (
        cloud.value === newBundle?.cloud &&
        miniPrograms.value === newBundle?.miniPrograms &&
        storage.value === newBundle?.storage &&
        database.value === newBundle?.database &&
        store.value === newBundle?.store
      )
    })
    if (bundle) {
      newBundle = {
        database: bundle?.spec.database.value,
        cloud: bundle?.spec?.cloud.value,
        store: bundle?.spec?.store.value,
        storage: bundle?.spec?.storage.value,
        miniPrograms: bundle?.spec?.['mini-programs'].value,
        bundleId: bundle?._id,
      }
    } else {
      newBundle.bundleId = 'custom'
    }
    setBundle(newBundle)
  }

  return (
    <Dialog
      open={addProjectModalVisible}
      onOpenChange={(open) => {
        changeAddProjectModal(open)
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="选择项目"
            className={cn('w-[200px] justify-between', className)}
          >
            <Avatar className="mr-2 h-5 w-5">
              <AvatarImage
                src={currentProject?.logo}
                alt={currentProject?.name}
              />
              <AvatarFallback>
                {currentProject?.name?.slice(0, 1)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {currentProject?.name}
            <CaretSortIcon className="ml-auto h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="搜索项目..." />
            <CommandList>
              <CommandEmpty>未找到项目.</CommandEmpty>
              <CommandGroup key="当前选择" heading={'当前选择'}>
                {projects
                  .filter((item) => item._id === currentProject?._id)
                  .map((project: any) => (
                    <CommandItem
                      key={`selected-${project._id}`}
                      onSelect={() => {
                        // updateProjectId(project.slug)
                        setOpen(false)
                      }}
                      className="text-sm"
                    >
                      <Avatar className="mr-2 h-5 w-5">
                        <AvatarImage
                          src={project.logo}
                          alt={project.name}
                          className="grayscale"
                        />
                        <AvatarFallback>
                          {project.name?.slice(0, 1)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {project.name}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          currentProject?.appid === project.appid
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
              <CommandGroup key="我的项目" heading={'我的项目'}>
                {projects.map((project) => (
                  <CommandItem
                    key={project._id}
                    onSelect={() => {
                      // updateProjectId(project.appid)
                      setOpen(false)
                      setCurrentProject(project)
                    }}
                    className="text-sm"
                  >
                    <Avatar className="mr-2 h-5 w-5">
                      <AvatarImage
                        src={project.logo}
                        alt={project.name}
                        className="grayscale"
                      />
                      <AvatarFallback>
                        {project.name?.slice(0, 1)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {project.name}
                    <CheckIcon
                      className={cn(
                        'ml-auto h-4 w-4',
                        currentProject?.appid === project.appid
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <CommandSeparator />
            <CommandList>
              <CommandGroup>
                <DialogTrigger asChild>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false)
                      changeAddProjectModal(true)
                    }}
                  >
                    <PlusCircledIcon className="mr-2 h-5 w-5" />
                    创建项目
                  </CommandItem>
                </DialogTrigger>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <DialogContent className="max-w-[960px] w-[960px]">
        <DialogHeader>
          <DialogTitle>创建项目</DialogTitle>
          <DialogDescription>
            你可以选择一个项目类型，创建一个新项目，Suite
            是以项目为维度的会员管理工具，每个项目对应一个或多个平台的小程序，创建项目后可通过配置必要的参数，订阅后即可发布项目并上线小程序。
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="space-y-4 py-2 pb-4">
            <div className="space-y-2">
              <Label htmlFor="name">项目名称</Label>
              <Input
                id="name"
                placeholder="请输入您要创建的项目名称"
                value={name}
                autoFocus
                autoComplete="off"
                required
                onChange={(e) => {
                  setData({ ...data, name: e.target.value })
                }}
              />
            </div>
            <div className="relative space-y-2">
              <Label htmlFor="slug">项目AppId</Label>
              <Input
                id="appid"
                placeholder="请输入项目标识"
                value={appid}
                pattern="[a-zA-Z0-9\-]+"
                autoComplete="off"
                className={`${
                  slugError
                    ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 text-gray-900 placeholder-gray-300 focus:border-gray-500 focus:ring-gray-500'
                } block w-full rounded-r-md focus:outline-none sm:text-sm`}
                onChange={(e) => {
                  setSlugError(null)
                  setData({ ...data, appid: e.target.value })
                }}
              />
              {slugError && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <AlertCircleIcon
                    className="h-5 w-5 text-red-500"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {slugError && (
              <p className="mt-2 text-sm text-red-600" id="slug-error">
                {slugError}
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="plan">项目类型</Label>
              <Select
                value={scene}
                onValueChange={(value) => {
                  setData({ ...data, scene: value })
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择项目类型（选择后不可更改）" />
                </SelectTrigger>
                <SelectContent>
                  {scenes?.map((item) => (
                    <SelectItem key={item._id} value={`${item._id}`}>
                      {item.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative space-y-2">
              <Label htmlFor="subscription">订阅计划</Label>
              <div className="flex flex-row gap-1 justify-between">
                {bundles.map((item) => {
                  return (
                    <div
                      key={item._id}
                      onClick={() => handleBundleSelect(item)}
                      className={cn(
                        'flex-1 relative overflow-hidden rounded-[10px] border border-stroke bg-white shadow-pricing dark:border-dark-3 dark:bg-dark-2 p-3 pt-1 cursor-pointer',
                        {
                          'border-primary': item._id === bundle.bundleId,
                        },
                      )}
                    >
                      {item._id === bundle.bundleId && (
                        <div className="absolute right-3 top-2">
                          <BadgeCheck className="text-primary text-base" />
                        </div>
                      )}
                      <span className="mb-3 block text-lg font-semibold text-primary">
                        {item.displayName}
                      </span>
                      {item._id === 'custom' ? (
                        <BundleSlider
                          data={billingResourceOptionsRes?.data ?? []}
                          bundle={bundle || {}}
                          onConfirm={handleCustomBundleSelect}
                        >
                          <div className="relative">
                            <p className="text-xs text-body-color dark:text-dark-6">
                              云服务：自定义
                            </p>
                            <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                              数据库：
                              {'自定义'}
                            </p>
                            <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                              云店铺：
                              {bundle.bundleId === 'custom'
                                ? `${bundle.store} 个`
                                : '自定义'}
                            </p>
                            <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                              小程序：
                              {bundle.bundleId === 'custom'
                                ? `${bundle.miniPrograms} 个`
                                : '自定义'}
                            </p>
                            <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                              云存储：
                              {bundle.bundleId === 'custom'
                                ? `${formatSize(bundle.storage * 1024 * 1024)}`
                                : '自定义'}
                            </p>
                          </div>
                        </BundleSlider>
                      ) : (
                        <div className="relative">
                          <p className="text-xs text-body-color dark:text-dark-6">
                            云服务：
                            {item.displayName}
                          </p>
                          <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                            数据库：
                            {item.displayName}
                          </p>
                          <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                            可创建 {item.spec.store.value} 个云店铺
                          </p>
                          <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                            可创建 {item.spec['mini-programs'].value} 个小程序
                          </p>
                          <p className="mt-2 text-xs text-body-color dark:text-dark-6">
                            云存储最大容量{' '}
                            {formatSize(item.spec.storage.value * 1024 * 1024)}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="flex sm:justify-between justify-between">
          <div className="flex items-center">
            <span className="mr-2 flex text-center text-lg font-semibold text-grayModern-600">
              <p>账户余额{':'}</p>
              <p className="ml-1">{formatPrice(accountRes?.data?.balance)}</p>
            </span>
            <ChargeButton>
              <p className="!mr-2 cursor-pointer text-lg font-semibold text-blue-600">
                立即充值
              </p>
            </ChargeButton>
          </div>
          <div className="flex-1 flex flex-row items-center space-x-2 justify-end">
            <div className="!mx-2 flex items-center">
              {!calculating ? (
                <span>
                  <span className="w-36 text-center text-xl font-semibold text-error-500">
                    <span className="font-number">{`${formatOriginalPrice(
                      totalPrice,
                      6,
                    )} / `}</span>
                    小时
                  </span>
                  <span className="mx-1 text-[13px] font-medium text-grayModern-600">
                    <span className="font-number">{`( ${formatOriginalPrice(
                      totalPrice * 24 * 30,
                    )} / `}</span>
                    月<span className="font-number">{')'}</span>
                  </span>
                </span>
              ) : (
                <span className="mr-2 flex w-36 justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </span>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => changeAddProjectModal(false)}
            >
              取消
            </Button>
            <Button
              disabled={saving || totalPrice > accountRes?.data?.balance!}
              type="submit"
              onClick={handleSubmit}
            >
              {totalPrice > accountRes?.data?.balance! && '余额不足'}
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? '创建中...' : '确定'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function BundleSlider({
  children,
  data,
  bundle,
  onConfirm,
}: {
  children: React.ReactElement
  data: TPlanOption[]
  onConfirm?: (bundle: TypeBundle) => void
  bundle?: TypeBundle
}) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [bundleValue, setBundleValue] = useState<TypeBundle>(
    bundle as TypeBundle,
  )

  return (
    <>
      {React.cloneElement(children, {
        onClick: () => {
          setIsOpen(true)
        },
      })}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>自定义订阅计划</DialogTitle>
            <DialogDescription>
              <div className="flex flex-col items-center justify-between space-y-3 p-3 dark:bg-[#F6F8F9]">
                {data
                  .filter((i) => i.type !== BundleType['band-width'])
                  .map((item) => {
                    return (
                      <div
                        className="w-full flex h-10 items-center"
                        key={item._id}
                      >
                        <div className="w-16">{BundleTypeText[item.type]}</div>
                        <div className="flex-1 ml-2 mr-6">
                          <Slider
                            onlyMarkValue
                            className="team-bundle-slider"
                            value={
                              get(bundleValue, camelCase(item.type)) ??
                              item.specs[0].value
                            }
                            getIntervalConfig={(_range, _index) => ({
                              width: `${100 / item.specs.length}%`,
                            })}
                            min={item.specs[0].value}
                            max={item.specs[item.specs.length - 1].value}
                            marks={item.specs.reduce((acc: any, shop: any) => {
                              acc[shop.value] = shop.label
                              return acc
                            }, {})}
                            onChange={(value) => {
                              setBundleValue((prev) => ({
                                ...prev,
                                [camelCase(item.type)]: value as number,
                              }))
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                onConfirm?.(bundleValue)
                setIsOpen(false)
              }}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
